
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";

export type SubscriptionTier = 'free' | 'basic' | 'growth' | 'professional';
type UserRole = Database['public']['Enums']['user_role'];

interface Profile {
  subscription_tier: SubscriptionTier | 'trainee';
  resources_used_this_month: number;
  total_resources_created: number;
  billing_status?: string;
  role: UserRole;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export const useProfile = (userId: string | undefined) => {
  const { toast } = useToast();
  
  return useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) return null;

      // Get both profile and user data
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("subscription_tier, resources_used_this_month, total_resources_created, billing_status, role, email, first_name, last_name")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        throw profileError;
      }
      
      // Get the user's email from auth
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email;
      
      // If email is missing in profile, use the one from auth
      if (profile && !profile.email && userEmail) {
        return {
          ...profile,
          email: userEmail
        };
      }

      return profile ?? {
        subscription_tier: 'trainee',
        resources_used_this_month: 0,
        total_resources_created: 0,
        billing_status: 'active',
        role: 'trainee' as UserRole,
        email: userEmail || null,
        first_name: null,
        last_name: null
      };
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // Consider profile data fresh for 5 minutes
  });
};
